import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Button, Space, Table } from 'antd'
import { PictureOutlined, SearchOutlined } from '@ant-design/icons'
import { ColumnsType } from 'antd/lib/table'

import { path, useColumnSearch } from 'src/libs'
import { Guard } from 'src/features/session/guard'

import {
  useTaskListGate,
  useTasksList,
  useTasksTypesList,
} from 'src/pages/worldCup2026/tasks/hooks'
import { onChangeTaskList } from 'src/pages/worldCup2026/tasks/model'
import { Task } from 'src/pages/worldCup2026/tasks/types'
import { TASKS_TYPES_NAMES } from 'src/pages/worldCup2026/const'
import {
  ChangeTaskModal,
  ClientTaskModal,
} from 'src/pages/worldCup2026/tasks/widgets'
import {
  ApproveButton,
  RejectButton,
} from 'src/pages/worldCup2026/tasks/components'

export const TaskList = () => {
  const [isChangeTaskModalOpen, setIsChangeTaskModalOpen] = useState(false)
  const [clientTaskCurrent, setClientTaskCurrent] = useState<{
    data: Task
  } | null>(null)
  const { tasks, loading, totalCountOfTasks } = useTasksList()
  const { tasksTypes } = useTasksTypesList()

  useTaskListGate()

  const taskTypeFilters = useMemo(
    () =>
      tasksTypes.map((item) => ({
        value: item.taskType,
        text: TASKS_TYPES_NAMES[
          item.taskType as keyof typeof TASKS_TYPES_NAMES
        ],
      })),
    [tasksTypes],
  )

  const clientIdSearch = useColumnSearch<Task>({
    placeholder: 'clientID',
    customRender: (highlightedValue, record) => (
      <Link
        to={path.clients.details(String(record.client.clientId))}
        style={{ textDecoration: 'none' }}
      >
        {highlightedValue}
      </Link>
    ),
  })
  const clientFullNameSearch = useColumnSearch<Task>({
    placeholder: 'client Fullname',
  })
  const clientEmailSearch = useColumnSearch<Task>({
    placeholder: 'client Email',
  })

  const columns: ColumnsType<Task> = [
    {
      title: 'Client ID',
      dataIndex: ['client', 'clientId'],
      ...clientIdSearch,
    },
    {
      title: 'Name',
      dataIndex: ['client', 'fullName'],
      ...clientFullNameSearch,
    },
    {
      title: 'Email',
      dataIndex: ['client', 'email'],
      onCell: () => ({
        style: {
          whiteSpace: 'normal',
          wordBreak: 'break-word',
        },
      }),
      ...clientEmailSearch,
    },
    {
      title: 'CheckType',
      dataIndex: 'checkType',
      width: 350,
      render: (type, record) => {
        if (type === 'image') {
          return (
            <Space>
              <strong>{type}</strong>
              {record.status === 'check' && (
                <Guard permission="world_cup_2026_task_update">
                  <PictureOutlined
                    style={{ cursor: 'pointer' }}
                    onClick={() => {
                      setClientTaskCurrent({ data: record })
                    }}
                  />
                </Guard>
              )}
            </Space>
          )
        }
        if (type === 'link') {
          return (
            <Space
              direction="vertical"
              style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}
            >
              <strong>{type}</strong>
              <Link to={record.checkValue} target="_blank">
                {record.checkValue}
              </Link>
            </Space>
          )
        }
        return <strong>{type}</strong>
      },
    },
    {
      title: 'Task',
      dataIndex: 'taskType',
      render: (item: string) =>
        TASKS_TYPES_NAMES[item as keyof typeof TASKS_TYPES_NAMES],
      filters: taskTypeFilters,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      filters: [
        {
          text: 'new',
          value: 'check',
        },
        {
          text: 'rejected',
          value: 'declined',
        },
        {
          text: 'accepted',
          value: 'accepted',
        },
      ],
      filterMultiple: false,
      defaultFilteredValue: ['check'],
    },
    {
      title: 'Approve',
      fixed: 'right',
      align: 'center',
      render: (record: Task) => {
        if (record.status !== 'check') return null
        return <ApproveButton id={record.id} />
      },
    },
    {
      title: 'Reject',
      fixed: 'right',
      align: 'center',
      render: (record: Task) => {
        if (record.status !== 'check') return null
        return <RejectButton id={record.id} />
      },
    },
    {
      title: 'Actions',
      fixed: 'right',
      align: 'center',
      render: (record: Task) => {
        if (record.status !== 'check') return null
        return (
          <Guard permission="world_cup_2026_task_update">
            <Button
              onClick={() => {
                setClientTaskCurrent({ data: record })
              }}
            >
              <SearchOutlined style={{ fontSize: 16 }} />
            </Button>
          </Guard>
        )
      },
    },
  ]

  return (
    <>
      <Guard permission="world_cup_2026_task_edit">
        <Button
          color="orange"
          size="large"
          variant="solid"
          onClick={() => setIsChangeTaskModalOpen(true)}
          style={{ alignSelf: 'flex-start', color: 'black', marginBottom: 24 }}
        >
          Change tasks
        </Button>
      </Guard>
      <Table
        dataSource={tasks}
        bordered={true}
        loading={loading}
        rowKey="id"
        columns={columns}
        pagination={{
          showSizeChanger: true,
          total: totalCountOfTasks,
        }}
        onChange={(pagination, filters, sorter: any) =>
          onChangeTaskList({
            page: pagination.current,
            limit: pagination.pageSize,
            orderBy: sorter?.field || sorter?.columnKey,
            orderDirection: sorter?.order,
            filters,
          })
        }
      />
      <ChangeTaskModal
        isVisible={isChangeTaskModalOpen}
        onClose={() => setIsChangeTaskModalOpen(false)}
      />
      {clientTaskCurrent && (
        <ClientTaskModal
          isVisible={Boolean(clientTaskCurrent)}
          onClose={() => setClientTaskCurrent(null)}
          data={clientTaskCurrent.data}
        />
      )}
    </>
  )
}
